import * as graphics from "@client/app/graphics";
import {Shell} from "@client/components/Shell";
import * as analytics from "@client/shared/analytics";
import {Static} from "@client/shared/components/Static";
import {useSiteContext, useStaticURL} from "@client/site/context";
import {RecipeShare} from "@client/templates/Share";
import {classNames, server} from "@reactivated";
import React from "react";

export function Template(props: server.journal.views.SuperSmoothies) {
    const STATIC_URL = useStaticURL();
    const {request} = useSiteContext();
    analytics.useTrackPageView();
    // Parse share_uuid from URL if present (e.g., /super-smoothies/<share_uuid>/)
    const urlShareUuid = React.useMemo(() => {
        const url = new URL(request.url);
        const match = url.pathname.match(/^\/super-smoothies\/([^/]+)\/?$/);
        return match?.[1] ?? null;
    }, [request.url]);

    // Find the recipe matching the URL share_uuid
    const initialRecipeUuid = React.useMemo(() => {
        if (urlShareUuid == null) return null;
        const recipe = props.recipes.find((r) => r.share_uuid === urlShareUuid);
        return recipe?.recipe.uuid ?? null;
    }, [urlShareUuid, props.recipes]);

    const [entitlementEmail, setEntitlementEmail] = React.useState(
        props.is_public == true || urlShareUuid != null ? "" : props.entitlement_email,
    );

    // Modal state
    const [showModal, setShowModal] = React.useState(entitlementEmail == null);
    const [email, setEmail] = React.useState("");
    const [error, setError] = React.useState<string | null>(null);

    // Track collection view
    React.useEffect(() => {
        void analytics.track("RECIPE_COLLECTION_VIEW", {
            recipe_entitlement_email: props.entitlement_email ?? undefined,
        });
    }, []);
    const hasTrackedScroll = React.useRef(false);

    // Track scroll analytics
    React.useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 200 && !hasTrackedScroll.current) {
                void analytics.track("SCROLLED_RECIPE_COLLECTION");
                hasTrackedScroll.current = true;
                // Remove listener after tracking to avoid further checks
                window.removeEventListener("scroll", handleScroll);
            }
        };

        window.addEventListener("scroll", handleScroll);

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []); // Empty dependency array ensures this runs once on mount and cleans up on unmount

    const [selectedRecipeUuid, setSelectedRecipeUuid] = React.useState<string | null>(
        initialRecipeUuid,
    );

    const selectedRecipe =
        selectedRecipeUuid != null
            ? props.recipes.find(
                  (recipeShare) => recipeShare.recipe.uuid === selectedRecipeUuid,
              )
            : null;

    // Handle browser back button
    React.useEffect(() => {
        const handlePopState = () => {
            setSelectedRecipeUuid(null);
        };

        window.addEventListener("popstate", handlePopState);

        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    }, []);

    // Modal component
    const unlockModal = (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30">
            <form
                className="relative mx-4 mb-20 w-full max-w-md rounded-2xl bg-white px-6 py-8 shadow-xl"
                onSubmit={async (e) => {
                    e.preventDefault();
                    const result = await server.journal.day.verify_purchase_entitlement(
                        {
                            form: email,
                        },
                    );

                    if (result.type == "success" && result.data != false) {
                        setEntitlementEmail(email);
                        setShowModal(false);
                        setError(null);
                        analytics.identify(result.data);
                        void analytics.track("VERIFIED_RECIPE_COLLECTION", {
                            recipe_entitlement_email: email,
                        });
                    } else {
                        setError("Invalid email");
                        void analytics.track("RECIPE_COLLECTION_VERIFICATION_ERROR", {
                            recipe_entitlement_email: email,
                        });
                    }
                }}
            >
                <h2 className="mb-2 text-2xl font-medium text-gray-900">
                    Unlock Super Smoothies
                </h2>
                <p className="mb-3 text-gray-700">
                    Enter the email you used to purchase the book.
                </p>
                <label
                    className="mb-1 block text-sm font-medium text-gray-700"
                    htmlFor="unlock-email"
                >
                    Email address
                </label>
                <div className="relative mb-2">
                    <input
                        id="unlock-email"
                        type="email"
                        className={classNames(
                            "w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base",
                            error != null ? "border-red-700" : "",
                        )}
                        placeholder="your.email@example.com"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                    {error != null && (
                        <p className="mt-2 px-1 text-base text-red-600">{error}</p>
                    )}
                </div>

                <p className="mt-0 mb-3 px-1 text-sm font-medium text-green-700">
                    No payment required.
                </p>
                <div className="mb-4 flex gap-2">
                    <button
                        className="flex-1 rounded-lg bg-gray-100 py-2 font-medium text-gray-700 transition hover:bg-gray-200"
                        onClick={() => {
                            setShowModal(false);
                            void analytics.track("CLICKED_PREVIEW_RECIPE_COLLECTION");
                        }}
                        type="button"
                    >
                        Preview
                    </button>
                    <button
                        className="shrink rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition hover:bg-blue-700"
                        type="submit"
                    >
                        Get Free Access
                    </button>
                </div>
                {props.product_link != null && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-center">
                        <span className="text-sm text-gray-700">
                            Don’t have the book yet?
                            <br />
                            <a
                                href={props.product_link}
                                onClick={analytics.trackLink(
                                    "CLICKED_PURCHASE_COOKBOOK_LINK",
                                    true,
                                )}
                                className="font-medium text-blue-700 underline"
                            >
                                Purchase Super Smoothies
                            </a>
                        </span>
                    </div>
                )}
            </form>
        </div>
    );

    return (
        <Shell>
            <div className="min-h-screen bg-stone-50">
                <Static />
                <meta charSet="UTF-8" />
                <meta
                    name="viewport"
                    content="viewport-fit=cover, width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"
                />
                <title>Super Smoothies | Joy Health Tracker</title>
                <link rel="icon" href={`${STATIC_URL}favicon.png`} type="image/png" />
                <style>
                    {`
                    body {
                        background-color: #fafaf9;
                    }
                    `}
                </style>
                {/*}
                <div className="flex items-center justify-center w-64 mx-auto">
                    <graphics.DarkLogoWide />
                </div>
                */}

                {/* Modal overlay */}
                {showModal && entitlementEmail == null && unlockModal}

                {/* Floating Verify Access button */}
                {!showModal && entitlementEmail == null && selectedRecipe == null && (
                    <button
                        className="fixed bottom-20 left-1/2 z-40 -translate-x-1/2 transform rounded-2xl bg-indigo-600 px-5 py-3 text-base font-medium text-white shadow-lg transition hover:bg-blue-700"
                        onClick={() => setShowModal(true)}
                    >
                        Verify Access
                    </button>
                )}

                <div className="container mx-auto px-4 py-3 pb-20">
                    {/* Show selected recipe details */}
                    {selectedRecipe && (
                        <div className="">
                            <div className="mb-3 flex items-center justify-center">
                                <button
                                    onClick={() => setSelectedRecipeUuid(null)}
                                    className="text-base text-gray-800 hover:text-gray-700"
                                >
                                    ← Back to collection
                                </button>
                            </div>
                            <RecipeShare share={selectedRecipe} type="embed" />
                        </div>
                    )}

                    {/* Recipe Grid */}
                    {!selectedRecipe && (
                        <>
                            <div className="mb-6 flex flex-col items-center">
                                <h2 className="text-center text-2xl font-light">
                                    {props.name}
                                </h2>
                                <h3 className="text-center text-sm text-gray-700">
                                    By{" "}
                                    <a
                                        onClick={analytics.trackLink(
                                            "CLICKED_AUTHOR_HANDLE",
                                            true,
                                        )}
                                        href="https://www.instagram.com/thejosephabell/"
                                        className="font-medium text-indigo-700 hover:underline"
                                    >
                                        @thejosephabell
                                    </a>
                                </h3>
                            </div>
                            <div
                                className={`grid grid-cols-2 gap-4 lg:grid-cols-3 ${
                                    entitlementEmail == null
                                        ? `[&>*:nth-child(n+3)]:pointer-events-none [&>*:nth-child(n+3)]:blur-xs lg:[&>*:nth-child(n+3)]:pointer-events-auto lg:[&>*:nth-child(n+3)]:blur-none lg:[&>*:nth-child(n+4)]:pointer-events-none lg:[&>*:nth-child(n+4)]:blur-xs`
                                        : ""
                                } `}
                            >
                                {props.recipes.map((recipeShare) => {
                                    const name = recipeShare.recipe.name.replace(
                                        "Smoothie",
                                        "",
                                    );

                                    return (
                                        <div
                                            key={recipeShare.recipe.uuid}
                                            className="cursor-pointer items-center overflow-hidden rounded-xl bg-white text-left transition-shadow duration-200 hover:shadow-md"
                                            onClick={() => {
                                                setSelectedRecipeUuid(
                                                    recipeShare.recipe.uuid,
                                                );
                                                // Push new state to browser history
                                                window.history.pushState(
                                                    {
                                                        recipeUuid:
                                                            recipeShare.recipe.uuid,
                                                    },
                                                    "",
                                                    window.location.href,
                                                );
                                                void analytics.track(
                                                    "SELECTED_COLLECTION_RECIPE",
                                                    {
                                                        recipe_name:
                                                            recipeShare.recipe.name,
                                                    },
                                                );
                                            }}
                                        >
                                            <div className="aspect-square overflow-hidden">
                                                {recipeShare.recipe.image?.image_uid !=
                                                null ? (
                                                    <img
                                                        src={`https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg/${recipeShare.recipe.image.image_uid}/public`}
                                                        alt={recipeShare.recipe.name}
                                                        className="size-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="flex size-full items-center justify-center bg-gray-200">
                                                        <span className="text-sm text-gray-400">
                                                            No image
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="p-3">
                                                <h3 className="mb-2 line-clamp-2 truncate text-base/5 font-extralight text-gray-900">
                                                    {name}
                                                </h3>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-1">
                                                        <span className="rounded-full bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-600">
                                                            {recipeShare.recipe
                                                                .protein_pg != null
                                                                ? `${Math.round(parseFloat(recipeShare.recipe.protein_pg))}g protein`
                                                                : "N/A"}
                                                        </span>
                                                    </div>
                                                    <div className="mb-0.5 text-right">
                                                        <span className="text-xs font-semibold text-gray-900">
                                                            {recipeShare.recipe
                                                                .calories_pg != null
                                                                ? `${Math.round(parseFloat(recipeShare.recipe.calories_pg))}`
                                                                : "N/A"}
                                                        </span>
                                                        <span className="ml-1 text-xs text-gray-500">
                                                            cal
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Sticky CTA at bottom */}
            <div
                className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-stone-50 px-3 py-2"
                style={{filter: "2blur(3px)"}}
            >
                <div className="flex flex-col items-center justify-center gap-1 py-1">
                    <graphics.DarkLogoWide className="h-auto w-[12em]" />
                    <div className="flex items-center space-x-3 text-xs text-pretty">
                        <span className="text-gray-700">Nutrition</span>
                        <div className="size-1 rounded-full bg-indigo-600" />
                        <span className="text-gray-700">Workouts</span>
                        <div className="size-1 rounded-full bg-indigo-600" />
                        <span className="text-gray-700">Habits</span>
                    </div>
                </div>
                {/*
                <div className="flex gap-2 items-center">
                    <div className="flex-1 flex gap-4 items-center">
                        <a className="" href={reverse("download")}>
                            <graphics.DarkLogoWide className="size-12" />
                        </a>
                        <div className="text-pretty space-x-2 flex items-center max-sm:text-xs max-sm:space-x-1 max-sm:-ml-2">
                            <span className="text-gray-700">Nutrition</span>
                            <div className="rounded-full size-1 bg-indigo-600" />
                            <span className="text-gray-700">Workouts</span>
                            <div className="rounded-full size-1 bg-indigo-600" />
                            <span className="text-gray-700">Habits</span>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            void analytics.track("CLICKED_DOWNLOAD_APP_FROM_SMOOTHIES");
                            window.location.href = reverse("download");
                        }}
                        type="button"
                        className="whitespace-nowrap rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                        Download App
                    </button>
                </div>
                */}
            </div>
        </Shell>
    );
}
